/**
 * Per-page titles, descriptions, social previews and canonical links.
 *
 * Copy lives in src/locales/english.json under `seo.*`. Public routes carry
 * `meta.seo` (a key under `seo.pages`) and `meta.public: true`; the door route
 * `/for/:door` carries `meta.seo: 'door'` and resolves the key from its
 * param. The router's beforeEach calls `resolvePageMeta` + `applyPageMeta`
 * on every navigation, so the tags always describe the page being shown.
 *
 * Pure apart from `applyPageMeta(doc, …)`, which takes the document as an
 * argument. No `@/` imports: tests run it under plain Node.
 */

export const DEFAULT_PUBLIC_SITE_URL = 'https://denvstack.com'

/** The public pages (seo keys) and the path each is served at. */
export const PUBLIC_PAGES = Object.freeze([
  { key: 'home', path: '/' },
  { key: 'owners', path: '/for/owners' },
  { key: 'agencies', path: '/for/agencies' },
  { key: 'developers', path: '/for/developers' },
  { key: 'engineering-leads', path: '/for/engineering-leads' },
  { key: 'self-managed', path: '/for/self-managed' },
  { key: 'pricing', path: '/pricing' },
  { key: 'marketplace', path: '/marketplace' },
  { key: 'platform', path: '/platform' },
  { key: 'security', path: '/security' },
  { key: 'contact', path: '/contact' },
  { key: 'terms', path: '/terms' },
  { key: 'privacy', path: '/privacy' },
  { key: 'refunds', path: '/refunds' },
  { key: 'aup', path: '/aup' },
  { key: 'dpa', path: '/dpa' },
  { key: 'subprocessors', path: '/subprocessors' },
  { key: 'securityOverview', path: '/legal/security' }
])

function pick(messages, dotted) {
  return dotted.split('.').reduce((o, k) => (o && typeof o === 'object' && k in o ? o[k] : undefined), messages)
}

/**
 * Route meta for a public page, from english.json — used in the route table
 * so `meta.title` / `meta.description` stay literal strings sourced from the
 * locale file. Throws on a missing key so a typo fails the build/tests.
 */
export function seoMeta(messages, key) {
  const page = pick(messages, `seo.pages.${key}`)
  if (!page || typeof page.title !== 'string' || typeof page.description !== 'string') {
    throw new Error(`seo.pages.${key} is missing a title or description`)
  }
  return { seo: key, public: true, title: page.title, description: page.description }
}

/** Absolute canonical URL for a path on the public site (no query, no hash, no trailing slash). */
export function canonicalUrl(siteUrl, path) {
  const base = String(siteUrl || DEFAULT_PUBLIC_SITE_URL).replace(/\/+$/, '')
  let p = String(path || '/').split(/[?#]/)[0]
  if (!p.startsWith('/')) p = `/${p}`
  if (p.length > 1) p = p.replace(/\/+$/, '')
  return p === '/' ? `${base}/` : `${base}${p}`
}

/**
 * Resolve everything the head needs for a route.
 * @param {{ path: string, params?: object, meta?: object }} route
 * @param {object} messages english.json
 * @param {string} siteUrl canonical host
 */
export function resolvePageMeta(route, messages, siteUrl) {
  const meta = (route && route.meta) || {}
  let key = meta.seo || null
  if (key === 'door') key = route.params && route.params.door
  const page = key ? pick(messages, `seo.pages.${key}`) : null
  const fallback = pick(messages, 'seo.default') || {}
  const title = (page && page.title) || meta.title || fallback.title || 'DenvStack'
  const description = (page && page.description) || meta.description || fallback.description || ''
  return {
    title,
    description,
    canonical: canonicalUrl(siteUrl, route && route.path),
    ogType: 'website',
    siteName: pick(messages, 'seo.siteName') || 'DenvStack',
    twitterCard: 'summary',
    isPublic: Boolean(page && (meta.public || meta.seo === 'door'))
  }
}

/** The head tags `applyPageMeta` writes, as [selector-attr, key, value] — exported for tests. */
export function headTags(pm) {
  return [
    ['name', 'description', pm.description],
    ['property', 'og:title', pm.title],
    ['property', 'og:description', pm.description],
    ['property', 'og:url', pm.canonical],
    ['property', 'og:type', pm.ogType],
    ['property', 'og:site_name', pm.siteName],
    ['name', 'twitter:card', pm.twitterCard],
    ['name', 'twitter:title', pm.title],
    ['name', 'twitter:description', pm.description]
  ]
}

/** Write the title, meta tags and canonical link into `doc` (create or update; never duplicate). */
export function applyPageMeta(doc, pm) {
  if (!doc || !pm) return
  doc.title = pm.title
  const head = doc.head || doc.querySelector('head')
  for (const [attr, key, value] of headTags(pm)) {
    let el = doc.querySelector(`meta[${attr}="${key}"]`)
    if (!el) {
      el = doc.createElement('meta')
      el.setAttribute(attr, key)
      head.appendChild(el)
    }
    el.setAttribute('content', value || '')
  }
  let link = doc.querySelector('link[rel="canonical"]')
  if (!link) {
    link = doc.createElement('link')
    link.setAttribute('rel', 'canonical')
    head.appendChild(link)
  }
  link.setAttribute('href', pm.canonical)
}
